/**
 * Intervenant workspace.
 *
 * Only booked slots appear: there is nothing to deliver where nobody is
 * waiting. Validating updates the shared stock immediately, so the operator's
 * dashboard moves without anyone re-keying anything.
 */
import { useMemo, useState } from "react";
import { formatDayFr, formatNumberFr, type DeliverySlot } from "@soi/contracts";
import { ApiFailure } from "../lib/api.js";
import { useMe, useMyDeliveries, useToggleDelivery } from "../lib/queries.js";
import { useToast } from "../components/Toast.js";
import { Kpis } from "../components/Kpis.js";

export function Deliveries() {
  const { data: me } = useMe();
  const { data, isLoading } = useMyDeliveries();
  const toggle = useToggleDelivery();
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [state, setState] = useState("");
  const [day, setDay] = useState("");

  const items = data?.items ?? [];
  const days = useMemo(() => [...new Set(items.map((i) => i.day))].sort(), [items]);

  const filtered = useMemo(
    () =>
      items.filter((c) => {
        if (state === "fait" && !c.delivered) return false;
        if (state === "reste" && c.delivered) return false;
        if (day && c.day !== day) return false;
        if (search && !c.siteName.toLowerCase().includes(search.trim().toLowerCase())) return false;
        return true;
      }),
    [items, state, day, search],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, DeliverySlot[]>();
    for (const item of filtered) {
      const list = map.get(item.day) ?? [];
      list.push(item);
      map.set(item.day, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const done = items.filter((i) => i.delivered).length;

  const onToggle = async (item: DeliverySlot) => {
    try {
      await toggle.mutateAsync({ slotId: item.slotId, delivered: item.delivered });
      toast(
        item.delivered
          ? `Validation annulée — ${item.siteName} · ${item.time}`
          : `Poste livré validé — ${item.siteName} · ${item.time}`,
        item.delivered ? "annule" : "ok",
      );
    } catch (err) {
      toast(err instanceof ApiFailure ? err.message : "L'enregistrement a échoué.", "err");
    }
  };

  return (
    <div className="gestion">
      <div className="admin-main">
        <div className="wrap">
          <section className="panel">
            <h1>Mes livraisons</h1>
            <p className="lede">
              Bonjour {me?.displayName}. Seuls les créneaux réservés par un collaborateur
              apparaissent ici : validez chaque poste une fois livré, le stock est mis à jour
              immédiatement.
            </p>

            <Kpis
              items={[
                [formatNumberFr(items.length), "Rendez-vous réservés"],
                [formatNumberFr(done), "Postes livrés et validés"],
                [formatNumberFr(items.length - done), "Postes restant à livrer"],
                [`${items.length ? Math.round((100 * done) / items.length) : 0} %`, "Avancement"],
              ]}
            />

            <div className="toolbar">
              <input
                type="search"
                placeholder="Rechercher un bâtiment…"
                aria-label="Rechercher un bâtiment"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                aria-label="Filtrer par état"
                value={state}
                onChange={(e) => setState(e.target.value)}
              >
                <option value="">Tous les créneaux</option>
                <option value="reste">À livrer</option>
                <option value="fait">Déjà livrés</option>
              </select>
              <label className="f-label" htmlFor="liv-date">
                Journée
              </label>
              <select
                id="liv-date"
                aria-label="Filtrer par journée"
                value={day}
                onChange={(e) => setDay(e.target.value)}
              >
                <option value="">Toutes</option>
                {days.map((d) => (
                  <option key={d} value={d}>
                    {formatDayFr(d)}
                  </option>
                ))}
              </select>
              <span className="muted">
                {formatNumberFr(filtered.length)} créneau{filtered.length > 1 ? "x" : ""} affiché
                {filtered.length > 1 ? "s" : ""}
              </span>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                <div className="liv-liste">
                  {isLoading && <div className="empty">Chargement…</div>}

                  {!isLoading && byDay.length === 0 && (
                    <div className="empty">
                      {items.length
                        ? "Aucun créneau ne correspond à ce filtre."
                        : "Aucun rendez-vous réservé pour le moment."}
                      <br />
                      {!items.length && (
                        <span className="muted">
                          Vos {formatNumberFr(data?.assignedSlots ?? 0)} créneaux planifiés
                          apparaîtront ici au fur et à mesure que les collaborateurs réservent leur
                          rendez-vous.
                        </span>
                      )}
                    </div>
                  )}

                  {byDay.map(([d, list]) => (
                    <div className="liv-jour" key={d}>
                      <h3>{formatDayFr(d)}</h3>
                      <p className="liv-sub">
                        {formatNumberFr(list.filter((c) => c.delivered).length)} /{" "}
                        {formatNumberFr(list.length)} postes livrés
                      </p>
                      <div className="liv-slots">
                        {list.map((item) => (
                          <button
                            key={item.slotId}
                            type="button"
                            className={`liv-slot${item.delivered ? " fait" : ""}`}
                            data-testid={`delivery-${item.slotId}`}
                            data-delivered={item.delivered ? "true" : "false"}
                            disabled={toggle.isPending}
                            onClick={() => void onToggle(item)}
                          >
                            <span>
                              {item.time} · {item.siteName}
                            </span>
                            <span className="lieu">
                              {item.delivered
                                ? "Livré, cliquer pour annuler"
                                : "Cliquer pour valider la livraison"}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
